import {
  Box,
  Menu,
  Tooltip,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Close, DevicesFold, PriorityHigh } from '@mui/icons-material'
import classNames from 'classnames'
import camelcaseKeys from 'camelcase-keys'
import React, { useMemo } from 'react'
import {
  Datagrid,
  Filter,
  ListProps,
  Pagination,
  ReferenceField,
  TextField,
  TextInput,
  sanitizeFetchType,
  useQuery,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import List from '../../common/customized-ra-components/List'
import FilterProps from '../../common/FilterProps'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import {
  CheckIfAnyModuleHasError,
  CheckLastRefreshError,
  DeviceStatusesPreview,
} from '../common/modules/ModuleStatus'
import { TerminalPowerButton } from './terminal-list/TerminalPowerButton'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import PaperRoll from '../../../assets/icons/PaperRoll'
import { PrinterDto } from '../../../core/dto/device/common/modules/printer.dto'
import Button from '../../common/customized-mui-components/Button'
import { TerminalLockBulkButton } from './terminal-list/TerminalLockBulkButton'
import { TerminalUnlockBulkButton } from './terminal-list/TerminalUnlockBulkButton'
import { TerminalAttachEventRecurringBulkButton } from './terminal-list/TerminalAttachEventRecurringBulkButton'
import { TerminalDetachEventRecurringBulkButton } from './terminal-list/TerminalDetachEventRecurringBulkButton'
import { TerminalWorkingModeButton } from './terminal-list/TerminalWorkingModeButton'
import { TerminalWorkingModeBulkButton } from './terminal-list/TerminalWorkingModeBulkButton'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import QuickFilter from '../../common/QuickFilter'
import { TerminalUploadPackBulkButton } from './terminal-list/TerminalUploadPackBulkButton'
import { DeviceResetModulesBulkButton } from '../../common/DeviceResetModulesBulkButton'
import { useColorPalette } from '../../../core/devices/devices.utils'
import { DeviceStatusCheck } from '../../../core/enum/DeviceStatusCheck'

const useStyles = makeStyles((theme) => ({
  compactStatus: {
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 34,
    cursor: 'default',
  },
  iconError: {
    color: theme.palette.error.dark,
  },
  icon: {
    padding: 4,
  },
  iconContainer: {
    position: 'relative',
    width: 34,
  },
  statusIcon: {
    position: 'absolute',
    left: 5,
    top: 5,
    fontSize: '24px !important',
  },
  crossIcon: {
    position: 'absolute',
    left: 0,
    top: 0,
    fontSize: 34,
    opacity: 0.8,
  },
  exclamationIcon: {
    position: 'absolute',
    left: 2.5,
    top: 0,
    fontSize: 34,
  },
}))

const TerminalFilters = (props: FilterProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Filter {...props}>
      <TextInput source="name" alwaysOn autoFocus />
      {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
        <FilteredReferenceInput
          label="resources.palms.fields.categoryId"
          source="categoryIdWithDescendants"
          reference={ResourceName.DEVICE_CATEGORIES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'hierarchyString',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_TS_CANS) && (
        <FilteredReferenceInput
          label="resources.terminals.fields.tsCanId"
          source="tsCanId"
          reference={ResourceName.TS_CANS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            showFilter: true,
          }}
        />
      )}
      <QuickFilter source="eventActive" defaultValue />
    </Filter>
  )
}

const TerminalGridExpand = ({ ...props }) => {
  const { record, serverDateTime } = props
  return (
    <DeviceStatusesPreview
      jsonStatus={record.jsonStatus}
      deviceType={DeviceTypes.TERMINALS}
      referenceDateTime={serverDateTime}
    />
  )
}

const TerminalBulkActions = ({ ...props }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const TerminalLockAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalLockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const TerminalUnlockAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalUnlockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const TerminalLockRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalLockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const TerminalUnlockRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalUnlockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const TerminalWorkingModeAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalWorkingModeBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const TerminalWorkingModeRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalWorkingModeBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const TerminalAttachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalAttachEventRecurringBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const TerminalDetachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalDetachEventRecurringBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const TerminalUploadPackAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalUploadPackBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const TerminalUploadPackRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <TerminalUploadPackBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const TerminalResetModulesAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DeviceResetModulesBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          resource={ResourceName.TERMINALS}
        />
      )),
    [props.selectedIds],
  )

  return (
    <div>
      <Button
        onClick={handleClick}
        label="const.selectAction"
        variant="text"
        color="default"
        useSmallVersionBreakpoint={false}
      />
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
      >
        <TerminalLockAction />
        <TerminalLockRecurringAction />
        <TerminalUnlockAction />
        <TerminalUnlockRecurringAction />
        <TerminalWorkingModeAction />
        <TerminalWorkingModeRecurringAction />
        <TerminalAttachEventRecurringAction />
        <TerminalDetachEventRecurringAction />
        <TerminalResetModulesAction />
        <TerminalUploadPackAction />
        <TerminalUploadPackRecurringAction />
      </Menu>
    </div>
  )
}

const CompactStatus = ({ ...props }) => {
  const { record: terminal } = props
  const classes = useStyles()
  const translate = useTranslate()

  const jsonStatus =
    terminal?.jsonStatus &&
    camelcaseKeys(JSON.parse(terminal?.jsonStatus), { deep: true })
  const printerStatus = jsonStatus?.printerStatus as PrinterDto

  return (
    <Box
      className={classes.compactStatus}
      onClick={(event) => event.stopPropagation()}
    >
      {jsonStatus && (
        <Tooltip
          title={
            jsonStatus?.isDoorOpen
              ? translate('resources.enums.isCoverOpened.open')
              : translate('resources.enums.isCoverOpened.closed')
          }
        >
          <DevicesFold
            className={classNames(
              jsonStatus?.isDoorOpen && classes.iconError,
              classes.icon,
            )}
          />
        </Tooltip>
      )}
      {printerStatus && (
        <Tooltip
          title={
            //eslint-disable-next-line no-nested-ternary
            printerStatus?.paperNotPresent
              ? translate(
                  'resources.enums.printerPaperStatuses.paperNotPresent',
                )
              : printerStatus?.lowPaper
              ? translate('resources.enums.printerPaperStatuses.lowPaper')
              : translate('resources.enums.printerPaperStatuses.ok')
          }
        >
          <div className={classes.iconContainer}>
            <>
              <PaperRoll
                style={{
                  fill: 'transparent',
                  alignSelf: 'center',
                  opacity:
                    printerStatus?.paperNotPresent || printerStatus?.lowPaper
                      ? 0.5
                      : 1,
                }}
                className={classes.statusIcon}
              />
              {printerStatus?.paperNotPresent && (
                <Close fontSize="large" className={classes.crossIcon} />
              )}
              {!printerStatus?.paperNotPresent && printerStatus?.lowPaper && (
                <PriorityHigh
                  fontSize="large"
                  className={classes.exclamationIcon}
                />
              )}
            </>
          </div>
        </Tooltip>
      )}
      <TerminalPowerButton jsonStatus={jsonStatus} record={terminal} />
      <TerminalWorkingModeButton jsonStatus={jsonStatus} record={terminal} />
    </Box>
  )
}

export const TerminalList = ({ ...props }: ListProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()
  const colorPalette = useColorPalette(theme)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  const postRowStyle = (record: any) => {
    if (!serverDate) return {}

    const terminalError = CheckLastRefreshError(
      record?.lastRefresh,
      serverDate?.serverDateTime,
    )
    if (terminalError) return colorPalette(DeviceStatusCheck.ERROR)

    const terminalWarning = CheckIfAnyModuleHasError(
      record.jsonStatus,
      DeviceTypes.TERMINALS,
      serverDate?.serverDateTime,
    )
    if (terminalWarning) return colorPalette(DeviceStatusCheck.WARNING)
    return colorPalette(DeviceStatusCheck.SUCCESS)
  }

  return (
    <List
      {...props}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      exporter={false}
      filters={<TerminalFilters />}
      bulkActionButtons={<TerminalBulkActions />}
    >
      <Datagrid
        rowStyle={postRowStyle}
        size="small"
        rowClick={(id) => `${ResourceName.TERMINALS}/${id}/show`}
        isRowExpandable={(record) =>
          record?.jsonStatus !== undefined && record?.jsonStatus !== null
        }
        expand={
          <TerminalGridExpand serverDateTime={serverDate?.serverDateTime} />
        }
      >
        <TextField source="id" label="ID" />
        <TextField source="name" />
        {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link={false}
            sortBy="name"
          >
            <TextField source="hierarchyString" />
          </ReferenceField>
        )}
        {!smallScreen && (
          <ReferenceField
            source="disabledId"
            reference={ResourceName.VERIFIER_DEVICE_DISABLED}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        <CompactStatus source="status" sortable={false} />
      </Datagrid>
    </List>
  )
}
